import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import * as Lo from 'lodash';
import * as Yup from 'yup';
import {
  YupBoolean,
  YupDate,
  YupNumeric,
  YupObject,
  YupString,
  yupRequiredMessage,
} from 'motion-components';
import {
  convertFieldToBoolean,
  convertFieldToFloat,
  convertFieldToInt,
} from 'shared-pages/common/form-helpfunc';
import { CollectionOrderXrbInput } from '../../graphql';
import { gqlGetCollectionOrderXrbByOrderId } from '../queries';

type UseOrderXrbArgs = {
  isNew: boolean;
  isRebatteryLiion: boolean;
  idNum: number;
  data: any;
  t: (key: string) => string;
};

type PrepareXrbForMutationArgs = {
  mutValues: any;
  isRebatteryLiion: boolean;
  isNew: boolean;
  idNum: number;
};

export function prepareXrbForMutationData({
  mutValues,
  isRebatteryLiion,
  isNew,
  idNum,
}: PrepareXrbForMutationArgs): void {
  if (mutValues.xrbData && isRebatteryLiion) {
    const xrb = { ...mutValues.xrbData };
    convertFieldToInt(xrb, [
      'msdsFileId',
      'bmsFileId',
      'techSpecFileId',
      'pieceCount',
      'orgPieceCount',
      'wastereceptionpointId',
    ]);
    convertFieldToFloat(xrb, [
      'batteryWeight',
      'batteryHeight',
      'batteryLength',
      'batteryWidth',
      'orgBatteryWeight',
      'orgBatteryHeight',
      'orgBatteryLength',
      'orgBatteryWidth',
      'packagingHeight',
      'packagingLength',
      'packagingWidth',
      'totalRequestWeight',
    ]);
    convertFieldToBoolean(xrb, [
      'certifiedPackagingIsAvailable',
      'replacementPackagingIsUsed',
      'packagingReturnRequired',
      'temperatureReadingRequired',
      'voltageReadingRequired',
      'collordPackagingReturnRequired',
      'technicianVerifiesData',
      'carAccident',
      'fire',
      'naturalDisasters',
      'vandalism',
      'batteryLiquidCoolingSystemFailure',
      'noBmsData',
      'fireSmokeSignsSigns',
      'oxidationSigns',
      'leakageVentingSigns',
      'cannotDiagnose',
      'safetyDeviceTriggered',
      'easyLoadingAccess',
      'specialEquipmentRequired',
      'collectionPointHasEquipment',
      // orginize tab fields
      'appropriatePackaging',
      'aiIsAppropriatePackaging',
      'vermiculiteUsed',
      'aeroplastUsed',
      'plasticBagUsed',
      'polyethyleneUsed',
    ]);
    if (xrb.packagingDispatchDate && typeof xrb.packagingDispatchDate !== 'string') {
      xrb.packagingDispatchDate = new Date(xrb.packagingDispatchDate).toISOString();
    }

    mutValues.xrbData = Lo.pick(xrb, Lo.keys(new CollectionOrderXrbInput()));
    const pieceDetails = (xrb.pieceDetails || []).map((row: any) => {
      const r = { ...row };
      convertFieldToInt(r, ['pieceIndex', 'daysOutOfOperation', 'daysNotMoved']);
      if (r.chargeLevelDate && typeof r.chargeLevelDate !== 'string') {
        r.chargeLevelDate = new Date(r.chargeLevelDate).toISOString();
      }
      return r;
    });
    mutValues.xrbData.pieceDetails = pieceDetails;
    const fileDetails = (xrb.fileDetails || [])
      .filter((row: any) => row?.fileId != null && row?.fieldName)
      .map((row: any, index: number) => {
        const r = { ...row };
        convertFieldToInt(r, ['fileId', 'sortOrder']);
        return {
          fieldName: r.fieldName,
          fileId: r.fileId,
          sortOrder: r.sortOrder ?? index + 1,
          notes: r.notes || null,
        };
      });
    mutValues.xrbData.fileDetails = fileDetails;
    if (!isNew) {
      mutValues.xrbData.collectionOrderId = idNum;
    }
    mutValues.xrbData.aiFileDataExtraction = JSON.stringify(xrb.aiFileDataExtractionObject);
    delete mutValues.xrbData.aiFileDataExtractionObject;
    if (xrb.aiPackagingMaterialsCalculationsObject != null) {
      mutValues.xrbData.aiPackagingMaterialsCalculations = JSON.stringify(
        xrb.aiPackagingMaterialsCalculationsObject,
      );
    }
    delete mutValues.xrbData.aiPackagingMaterialsCalculationsObject;
  } else if (!isRebatteryLiion) {
    delete mutValues.xrbData;
  }
}

export function useOrderXrb({ isNew, isRebatteryLiion, idNum, data, t }: UseOrderXrbArgs) {
  const [xrbRawData, setXrbRawData] = useState<any>(null);
  const [formDataWithXrb, setFormDataWithXrb] = useState<any>();
  const [getXrb, { data: xrbRawData0, loading: xrbLoading }] = useLazyQuery(
    gqlGetCollectionOrderXrbByOrderId,
    {
      fetchPolicy: 'network-only',
    },
  );

  const xrbShape = {
    pieceCount: YupNumeric.required(yupRequiredMessage)
      .min(1, t('Το πεδίο δεν μπορεί να είναι μικρότερο από 1'))
      .max(10, t('Το πεδίο δεν μπορεί να είναι μεγαλύτερο από 10')),
    msdsFileId: YupNumeric.nullable(),
    bmsFileId: YupNumeric.nullable(),
    techSpecFileId: YupNumeric.nullable(),
    manufacturer: YupString.required(yupRequiredMessage),
    partCode: YupString.required(yupRequiredMessage),
    chemistry: YupString.required(yupRequiredMessage),
    cellGeometry: YupString.required(yupRequiredMessage),
    nominalEnergyEfficiency: YupString.required(yupRequiredMessage),
    nominalCapacity: YupString.required(yupRequiredMessage),
    nominalVoltage: YupString.required(yupRequiredMessage),
    batteryWeight: YupNumeric.required(yupRequiredMessage),
    batteryHeight: YupNumeric.required(yupRequiredMessage),
    batteryLength: YupNumeric.required(yupRequiredMessage),
    batteryWidth: YupNumeric.required(yupRequiredMessage),
    pieceDetails: Yup.array()
      .of(
        Yup.object().shape({
          pieceIndex: YupNumeric.nullable(),
          daysOutOfOperation: YupNumeric.nullable(),
          daysNotMoved: YupNumeric.nullable(),
          chargeLevel: YupString.nullable(),
          chargeLevelDate: YupDate.nullable(),
        }),
      )
      .nullable(),
    cablesSecured: YupBoolean.nullable(),
    safetyPlugInstalled: YupBoolean.nullable(),
    certifiedPackagingIsAvailable: YupBoolean.nullable(),
    certifiedPackagingCode: YupString.nullable().matches(
      /^[A-Za-z0-9/\-]*$/,
      t('Μόνο γράμματα, αριθμοί και το διαχωριστικό / επιτρέπονται'),
    ),
    packagingCondition: YupString.nullable(),
    packagingHeight: YupNumeric.nullable(),
    packagingLength: YupNumeric.nullable(),
    packagingWidth: YupNumeric.nullable(),
    replacementPackagingIsUsed: YupBoolean.nullable(),
    packagingReturnRequired: YupBoolean.nullable(),
    packagingDispatchDate: YupDate.nullable(),
    packagingInstructions: YupString.nullable(),
    temperatureReadingRequired: YupBoolean.nullable(),
    voltageReadingRequired: YupBoolean.nullable(),
    packagingTypeCode: YupString.nullable(),
    collordPackagingReturnRequired: YupBoolean.nullable(),
    wastereceptionpointId: YupNumeric.nullable(),
    bmsCharacterization: YupString.nullable(),
    bmsCharacterizationOther: YupString.nullable(),
    technicianAssessment: YupString.nullable(),
    technicianVerifiesData: YupBoolean.nullable(),
    technicianName: YupString.nullable(),
    technicianRegistrationNumber: YupString.nullable(),
    packagingForCriticallyDamaged: YupBoolean.nullable(),
    carAccident: YupBoolean.nullable(),
    fire: YupBoolean.nullable(),
    naturalDisasters: YupBoolean.nullable(),
    vandalism: YupBoolean.nullable(),
    batteryLiquidCoolingSystemFailure: YupBoolean.nullable(),
    noBmsData: YupBoolean.nullable(),
    fireSmokeSignsSigns: YupBoolean.nullable(),
    oxidationSigns: YupBoolean.nullable(),
    leakageVentingSigns: YupBoolean.nullable(),
    cannotDiagnose: YupBoolean.nullable(),
    safetyDeviceTriggered: YupBoolean.nullable(),
    additionalObservations: YupString.nullable(),
    easyLoadingAccess: YupBoolean.nullable(),
    specialEquipmentRequired: YupBoolean.nullable(),
    collectionPointHasEquipment: YupBoolean.nullable(),
    equipmentDescription: YupString.nullable(),
    otherAccessNotes: YupString.nullable(),
    fileDetails: Yup.array()
      .of(
        Yup.object().shape({
          fieldName: YupString.nullable(),
          fileId: YupNumeric.nullable(),
          sortOrder: YupNumeric.nullable(),
          notes: YupString.nullable(),
          fileRep: YupObject.nullable(),
        }),
      )
      .nullable(),
    msdsFileRep: YupObject.nullable(),
    bmsFileRep: YupObject.nullable(),
    techSpecFileRep: YupObject.nullable(),
    // Organize Tab Fields -----------------
    orgChemistry: YupString.nullable(),
    orgBatteryWeight: YupNumeric.nullable(),
    orgPieceCount: YupNumeric.nullable(),
    orgBatteryHeight: YupNumeric.nullable(),
    orgBatteryLength: YupNumeric.nullable(),
    orgBatteryWidth: YupNumeric.nullable(),
  };

  /** Derive xrbRawData from lazy query result (replaces deprecated onCompleted) */
  useEffect(() => {
    const raw = xrbRawData0?.getCollectionOrderXrbByOrderId;
    if (!raw) return;
    const nextRaw = { ...raw };
    const aiFileDataExtraction = raw.aiFileDataExtraction;
    if (aiFileDataExtraction) {
      try {
        nextRaw.aiFileDataExtractionObject = JSON.parse(aiFileDataExtraction);
      } catch {
        // Αγνόησε μη έγκυρο JSON — εμφανίζεται ως raw string
      }
    }
    const aiPackagingMaterialsCalculations = raw.aiPackagingMaterialsCalculations;
    if (aiPackagingMaterialsCalculations) {
      try {
        nextRaw.aiPackagingMaterialsCalculationsObject = JSON.parse(
          aiPackagingMaterialsCalculations,
        );
      } catch {
        // Αγνόησε μη έγκυρο JSON — εμφανίζεται ως raw string
      }
    }
    setXrbRawData(nextRaw);
  }, [xrbRawData0]);

  /** Fetch xrbData when existing record is loaded and isRebatteryLiion */
  useEffect(() => {
    if (!isNew && isRebatteryLiion && data?.id) {
      console.log('Fetching extention record xrb by order id ...');
      getXrb({ variables: { id: data.id } });
    } else if (!isNew && !isRebatteryLiion && data) {
      setFormDataWithXrb(data);
    }
  }, [isNew, isRebatteryLiion, data?.id, data, getXrb]);

  /** Merge xrbData into form data when both are loaded; normalize pieceDetails from API */
  useEffect(() => {
    console.log('Merging extention record xrb to order ...');
    if (!isNew && isRebatteryLiion && data && xrbRawData) {
      const xrb = { ...xrbRawData };
      const details = xrb.collectionOrderXrbPieceDetails;
      if (details?.length) {
        const sorted = [...details].sort((a, b) => (a.pieceIndex ?? 0) - (b.pieceIndex ?? 0));
        xrb.pieceDetails = sorted.map((d) => ({
          pieceIndex: d.pieceIndex,
          daysOutOfOperation: d.daysOutOfOperation,
          daysNotMoved: d.daysNotMoved,
          chargeLevel: d.chargeLevel,
          chargeLevelDate: d.chargeLevelDate ?? null,
        }));
      } else {
        const n = Math.max(1, parseInt(xrb.pieceCount, 10) || 1);

        xrb.pieceDetails = Array.from({ length: n }, (_, i) => ({
          pieceIndex: i + 1,
          daysOutOfOperation: null,
          daysNotMoved: null,
          chargeLevel: null,
          chargeLevelDate: null,
        }));
      }
      const fileDetails = Array.isArray(xrb.fileDetails) ? xrb.fileDetails : [];
      xrb.fileDetails = [...fileDetails]
        .filter((f) => f?.fileId != null && f?.fieldName)
        .sort((a, b) => {
          const sortA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
          const sortB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
          if (sortA !== sortB) return sortA - sortB;
          return (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER);
        })
        .map((f) => ({
          ...f,
          pendingFileName: f.fileRep?.fileName ?? f.pendingFileName ?? null,
        }));

      // Προσθήκη virtual rows για κάθε expected fieldName που δεν έχει ήδη row
      // ώστε να μην μαρκαριστεί η φόρμα ως dirty από τον FileRepoFieldList useEffect
      const EXPECTED_FILE_FIELD_NAMES = [
        'accessPhotoFileId',
        'batteryPhotoFileId',
        'packagingPhotoFileId',
        'labelsPhotoFileId',
        'technicianCertificateFileId',
      ];
      for (const fn of EXPECTED_FILE_FIELD_NAMES) {
        if (!xrb.fileDetails.some((r: any) => r?.fieldName === fn)) {
          xrb.fileDetails.push({ fieldName: fn, fileId: null, sortOrder: 1, notes: null, pendingFileName: null });
        }
      }
      xrb.certifiedPackagingIsAvailable = xrb.certifiedPackagingIsAvailable ?? true;
      setFormDataWithXrb({ ...data, xrbData: xrb });
    }
  }, [isNew, isRebatteryLiion, data, xrbRawData]);

  const prepareXrbForMutation = useCallback(
    (mutValues: any) => {
      prepareXrbForMutationData({ mutValues, isRebatteryLiion, isNew, idNum });
    },
    [idNum, isNew, isRebatteryLiion],
  );

  const fetchXrbByOrderId = useCallback(
    (orderId: number) => getXrb({ variables: { id: orderId } }),
    [getXrb],
  );

  const getDefaultXrbForNewRecord = useCallback(
    () => ({
      pieceCount: 1,
      certifiedPackagingIsAvailable: true,
      // Αρχικοποίηση για boolean fields
      cablesSecured: null,
      safetyPlugInstalled: false,
      replacementPackagingIsUsed: false,
      packagingReturnRequired: false,
      packagingDispatchDate: null,
      packagingInstructions: null,
      temperatureReadingRequired: false,
      voltageReadingRequired: false,
      packagingTypeCode: null,
      collordPackagingReturnRequired: false,
      wastereceptionpointId: null,
      packagingForCriticallyDamaged: false,
      carAccident: false,
      fire: false,
      naturalDisasters: false,
      vandalism: false,
      batteryLiquidCoolingSystemFailure: false,
      noBmsData: false,
      fireSmokeSignsSigns: false,
      oxidationSigns: false,
      leakageVentingSigns: false,
      cannotDiagnose: false,
      safetyDeviceTriggered: false,

      easyLoadingAccess: false,
      specialEquipmentRequired: false,
      collectionPointHasEquipment: false,
      equipmentDescription: null,
      otherAccessNotes: null,
      technicianVerifiesData: false,
      // orginize tab fields
      appropriatePackaging: true,
      vermiculiteUsed: false,
      aeroplastUsed: false,
      plasticBagUsed: false,
      polyethyleneUsed: false,
      // Virtual rows για τα expected file fields ώστε να μην γίνει dirty η φόρμα από τον FileRepoFieldList
      fileDetails: [
        { fieldName: 'accessPhotoFileId', fileId: null, sortOrder: 1, notes: null, pendingFileName: null },
        { fieldName: 'batteryPhotoFileId', fileId: null, sortOrder: 1, notes: null, pendingFileName: null },
        { fieldName: 'packagingPhotoFileId', fileId: null, sortOrder: 1, notes: null, pendingFileName: null },
        { fieldName: 'labelsPhotoFileId', fileId: null, sortOrder: 1, notes: null, pendingFileName: null },
        { fieldName: 'technicianCertificateFileId', fileId: null, sortOrder: 1, notes: null, pendingFileName: null },
      ],
    }),
    [],
  );

  const resetFormDataWithXrb = useCallback(() => {
    setFormDataWithXrb(undefined);
    setXrbRawData(null);
  }, []);

  return {
    xrbShape,
    formDataWithXrb,
    xrbLoading,
    prepareXrbForMutation,
    fetchXrbByOrderId,
    getDefaultXrbForNewRecord,
    resetFormDataWithXrb,
  };
}
